import { memo, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";
import { PreloadedQuery } from "react-relay";

import SearchIcon from "@atlaskit/icon/glyph/search";
import Lozenge from "@atlaskit/lozenge";
import { media } from "@atlaskit/primitives/responsive";
import Spinner from "@atlaskit/spinner";

import { useSearchSuggestionsContentfulQuery } from "@/__generated_contentful__/useSearchSuggestionsContentfulQuery.graphql";
import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics.ts";
import { wacToken } from "@/libraries/design/foundations";

import {
    SearchSuggestion,
    useSearchSuggestions
} from "../../hooks/contentful-relay-hooks/use-search-suggestions-contentful";

const suggestionContainerStyle = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#FFFFFF",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #6B778C",
    // eslint-disable-next-line local/ensure-wac-token-usage
    filter: "drop-shadow(0px 3px 10px rgba(0, 0, 0, 0.25))",
    textAlign: "left",
    alignSelf: "stretch",
    maxHeight: "17.75rem",
    overflowY: "auto",
    position: "relative",
    zIndex: "2",
    borderRadius: "7px"
});

const suggestionSpinnerStyle = css({
    padding: "8px 6px"
});

const suggestionListStyle = css({
    "display": "flex",
    "alignItems": "center",
    "cursor": "pointer",
    "&:hover": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#EAEAEA99"
    },
    "&[aria-selected=true]": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#EAEAEA99"
    },
    "&:focus": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#EAEAEA99"
    },
    "&:active": {
        // eslint-disable-next-line local/ensure-wac-token-usage
        background: "#EAEAEA99"
    },
    [media.below.sm]: {
        paddingInlineStart: "10px",
        paddingInlineEnd: "10px",
        justifyContent: "space-between"
    }
});

const suggestionItemStyle = css({
    justifyContent: "flex-start",
    fontSize: "1rem",
    fontWeight: "500",
    lineHeight: "24px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#000000",
    fontFamily: wacToken("font.charlie.text"),
    padding: "8px 6px",
    [media.below.sm]: {
        fontSize: "0.875rem"
    }
});

const searchIconStyle = css({
    height: "54px",
    width: "55px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#808080",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
});

const searchIconResultsStyle = css({
    [media.below.sm]: {
        display: "none"
    }
});

type TemplateFuzzySearchIconProps = {
    variant?: "results";
};

const TemplateFuzzySearchIcon = memo(
    ({ variant }: TemplateFuzzySearchIconProps) => {
        const intl = useIntl();
        return (
            <div
                css={[
                    searchIconStyle,
                    variant == "results" ? searchIconResultsStyle : null
                ]}
            >
                <SearchIcon
                    label={intl.formatMessage(
                        ConfluenceTemplateMessages.searchLabel
                    )}
                    size="medium"
                />
            </div>
        );
    }
);

const SuggestionItem = ({ suggestion }: { suggestion: SearchSuggestion }) => {
    return (
        <>
            <TemplateFuzzySearchIcon variant="results" />
            <div css={suggestionItemStyle}>{suggestion.name}</div>
            <span>
                {suggestion.type === "category" && (
                    <Lozenge appearance="success">
                        <FormattedMessage
                            id="confluence.main.area.cfind.fuzzysearch.label.category"
                            defaultMessage="CATEGORY"
                        />
                    </Lozenge>
                )}
                {suggestion.type === "template" && (
                    <Lozenge appearance="inprogress">
                        <FormattedMessage
                            id="confluence.main.area.cfind.fuzzysearch.label.template"
                            defaultMessage="TEMPLATE"
                        />
                    </Lozenge>
                )}
            </span>
        </>
    );
};

const currentSuggestionIsValid = (
    currentSuggestion?: SearchSuggestion | null,
    suggestions?: SearchSuggestion[]
) => {
    if (!currentSuggestion) return true;
    if (!suggestions) return false;
    return suggestions.includes(currentSuggestion);
};

const nextSuggestion = (
    suggestions: SearchSuggestion[] | undefined,
    currentSuggestion?: SearchSuggestion | null,
    onSelect?: (value: SearchSuggestion | null) => void
) => {
    if (!onSelect) return;
    let nextSuggestion = suggestions?.[0] ?? null;
    if (currentSuggestion) {
        const index = suggestions?.indexOf(currentSuggestion) ?? -1;
        if (index > -1) {
            nextSuggestion = suggestions?.[index + 1] ?? currentSuggestion;
        }
    }
    onSelect?.(nextSuggestion);
};

const prevSuggestion = (
    suggestions: SearchSuggestion[] | undefined,
    currentSuggestion?: SearchSuggestion | null,
    onSelect?: (value: SearchSuggestion | null) => void
) => {
    if (!onSelect) return;
    let prevSuggestion = suggestions?.[suggestions.length - 1] ?? null;
    if (currentSuggestion) {
        const index = suggestions?.indexOf(currentSuggestion) ?? -1;
        if (index > -1) {
            prevSuggestion = suggestions?.[index - 1] ?? currentSuggestion;
        }
    }
    onSelect?.(prevSuggestion);
};

const acceptSuggestion = (
    suggestion: SearchSuggestion,
    onSubmit?: (value: SearchSuggestion) => void
) => {
    onSubmit?.(suggestion);
    ConfluenceTemplateAnalytics.sendTemplateFuzzySearchPreviewAnalytic(
        // @ts-ignore: SearchSuggestion is a union type roughly equivalent to TemplateSearchItem
        suggestion
    );
};

export type TemplateFuzzySearchSuggestionsProps = {
    queryRefSuggestions: PreloadedQuery<useSearchSuggestionsContentfulQuery>;
    inputValue: string | SearchSuggestion | null;
    currentSuggestion?: SearchSuggestion | null;
    onSelect: (value: SearchSuggestion | null) => void;
    onSubmit: (value: SearchSuggestion) => void;
};

export const TemplateFuzzySearchSuggestions = ({
    currentSuggestion,
    queryRefSuggestions,
    inputValue,
    onSubmit,
    onSelect
}: TemplateFuzzySearchSuggestionsProps) => {
    const suggestions = useSearchSuggestions(queryRefSuggestions, inputValue);

    useEffect(() => {
        if (currentSuggestion) {
            document
                .getElementById(`option-${currentSuggestion.id}`)
                ?.scrollIntoView({
                    block: "nearest",
                    inline: "nearest"
                });
        }
    }, [currentSuggestion]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "ArrowDown") {
                event.preventDefault();
                nextSuggestion(suggestions, currentSuggestion, onSelect);
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                prevSuggestion(suggestions, currentSuggestion, onSelect);
            } else if (event.key === "Enter") {
                if (currentSuggestion) {
                    event.preventDefault();
                    acceptSuggestion(currentSuggestion, onSubmit);
                }
            }
        };
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [suggestions, currentSuggestion, onSelect, onSubmit]);

    useEffect(() => {
        if (inputValue) {
            ConfluenceTemplateAnalytics.sendTemplateFuzzySearchResultsAnalytic(
                (suggestions?.length ?? 0).toString()
            );
        }
    }, [inputValue, suggestions]);

    useEffect(() => {
        if (!currentSuggestionIsValid(currentSuggestion, suggestions)) {
            onSelect?.(null);
        }
    }, [currentSuggestion, suggestions, onSelect]);

    return (
        <div
            css={suggestionContainerStyle}
            id="suggestion-box"
            data-testid="template-fuzzy-search-suggestions"
            role="listbox"
            aria-labelledby={
                currentSuggestion ? `option-${currentSuggestion.id}` : undefined
            }
        >
            {suggestions?.map((suggestion) => (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
                <li
                    key={suggestion.id}
                    id={`option-${suggestion.id}`}
                    css={suggestionListStyle}
                    // Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details.
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role="value"
                    aria-label={`Select ${suggestion.name}`}
                    aria-selected={currentSuggestion?.id === suggestion.id}
                    onClick={() => acceptSuggestion(suggestion, onSubmit)}
                >
                    <SuggestionItem suggestion={suggestion} />
                </li>
            ))}
        </div>
    );
};

export const TemplateFuzzySearchSuggestionsLoading = () => {
    return (
        <div
            css={suggestionContainerStyle}
            id="suggestion-box"
            data-testid="template-fuzzy-search-suggestions"
            role="listbox"
        >
            <div
                role="alert"
                aria-label="Loading"
                data-testid="template-fuzzy-search-suggestions-spinner"
                css={suggestionSpinnerStyle}
            >
                <Spinner />
            </div>
        </div>
    );
};
